import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { BudgetScreen } from 'src/app/CreateBudget/CreateBudget'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import BudgetForm, {
  BudgetFlatFormValues,
} from 'src/components/BudgetForm/BudgetForm'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { ImageNames, OrganizationRoles } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { UserAssociation } from 'src/models/user.models'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { PostCreditCardRequest } from 'src/requests/postCreditCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'

interface BudgetRecurringContentProps {
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  rcnOptions: CreditCardOption[]
  postingCreditCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  initialFlatFields?: Partial<BudgetFlatFormValues>
  setScreen: (val: BudgetScreen) => void
  postCreditCard: (body: Partial<PostCreditCardRequest>) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  isFormDisabled: boolean
  requesting?: boolean
}

const BudgetRecurringContent: React.FC<BudgetRecurringContentProps> = (
  props,
) => {
  const translate = useTranslate()

  const { analytics } = useAnalyticsContext()
  const user = useSelector(selectUserListModelFromState)
  const showRequestBudget = props.requesting
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const budgetRecurringActiveImage = useColorOrGrayscaleImage(
    ImageNames.BudgetRecurringActive,
  )

  const onSuccessfulSubmit = (
    flatFields: BudgetFlatFormValues,
    recurrenceFields: RecurrenceFormValues,
  ): void => {
    showRequestBudget
      ? props.postCreditCard({
          creditCardId: flatFields.parentCreditCardId,
          approverEmail: flatFields.email,
          currency: flatFields.currency,
          displayName: flatFields.displayName,
          issuingLimitCents:
            flatFields.issuingLimitDollars !== undefined &&
            !flatFields.isUnlimited
              ? toCents(flatFields.issuingLimitDollars)
              : undefined,
          type: CreditCardType.Delegate,
          recurs: true,
          recurrence: {
            balanceCents:
              flatFields.issuingLimitDollars !== undefined
                ? toCents(flatFields.issuingLimitDollars)
                : 0,
            period: recurrenceFields.period,
            interval: recurrenceFields.interval,
            terminator: recurrenceFields.terminator,
            count:
              recurrenceFields.terminator === RecurrenceTerminator.Count
                ? recurrenceFields.count
                : undefined,
            until:
              recurrenceFields.terminator === RecurrenceTerminator.Date
                ? recurrenceFields.untilMoment?.format('YYYY-MM-DD')
                : undefined,
            byWeekDay:
              recurrenceFields.period === RecurrencePeriod.Weekly
                ? recurrenceFields.byWeekDay
                : undefined,
            byMonthDay:
              recurrenceFields.period === RecurrencePeriod.Monthly
                ? recurrenceFields.byMonthDay
                : undefined,
            byYearDay:
              recurrenceFields.period === RecurrencePeriod.Yearly
                ? recurrenceFields.byYearDay
                : undefined,
          },
          notes: flatFields.notes,
        })
      : props.postCreditCard({
          creditCardId: flatFields.creditCardId,
          emails: flatFields.emails,
          displayName: flatFields.displayName,
          issuingLimitCents:
            flatFields.issuingLimitDollars !== undefined &&
            !flatFields.isUnlimited
              ? toCents(flatFields.issuingLimitDollars)
              : undefined,
          type: CreditCardType.Delegate,
          recurs: true,
          recurrence: {
            balanceCents:
              flatFields.issuingLimitDollars !== undefined
                ? toCents(flatFields.issuingLimitDollars)
                : 0,
            period: recurrenceFields.period,
            interval: recurrenceFields.interval,
            terminator: recurrenceFields.terminator,
            count:
              recurrenceFields.terminator === RecurrenceTerminator.Count
                ? recurrenceFields.count
                : undefined,
            until:
              recurrenceFields.terminator === RecurrenceTerminator.Date
                ? recurrenceFields.untilMoment?.format('YYYY-MM-DD')
                : undefined,
            byWeekDay:
              recurrenceFields.period === RecurrencePeriod.Weekly
                ? recurrenceFields.byWeekDay
                : undefined,
            byMonthDay:
              recurrenceFields.period === RecurrencePeriod.Monthly
                ? recurrenceFields.byMonthDay
                : undefined,
            byYearDay:
              recurrenceFields.period === RecurrencePeriod.Yearly
                ? recurrenceFields.byYearDay
                : undefined,
          },
          notes: flatFields.notes,
        })
    /* Old analytics event - tbd if / when we delete */
    if (user !== undefined) {
      showRequestBudget
        ? analytics?.track('successfully request budget', {
            type_of_budget: 'Recurring',
            budget_name: flatFields.displayName,
            budget_limit: flatFields.isUnlimited
              ? 'Unlimited'
              : '$' + flatFields.issuingLimitDollars,
            active_until: !flatFields.isForever
              ? flatFields.untilMoment?.format('LLL')
              : undefined,
            reset_ends:
              recurrenceFields.terminator === RecurrenceTerminator.Count
                ? `${recurrenceFields.count} resets`
                : recurrenceFields.terminator === RecurrenceTerminator.Date
                  ? recurrenceFields.untilMoment?.format('YYYY/MM/DD')
                  : 'never ends',
          })
        : analytics?.track('Successfully create budget', {
            type_of_budget: 'Recurring',
            budget_name: flatFields.displayName,
            credit_limit: flatFields.isUnlimited
              ? 'Unlimited'
              : flatFields.issuingLimitDollars,
            active_until: flatFields.isForever
              ? 'Never ends'
              : flatFields.untilMoment?.format('YYYY-MM-DD'),
            is_recipient_me:
              flatFields.emails && user
                ? flatFields.emails.includes(user.email)
                : flatFields.email !== undefined
                  ? flatFields.email === user?.email
                  : undefined,
            recipient_count:
              flatFields.emails !== undefined
                ? flatFields.emails.length
                : flatFields.email !== undefined
                  ? 1
                  : 0,
            add_notes: !!flatFields.notes,
            recurrence_period: recurrenceFields.period,
            number_recurrences: recurrenceFields.count,
            issuer:
              creditCards &&
              flatFields.creditCardId &&
              creditCards[flatFields.creditCardId]
                ? creditCards[flatFields.creditCardId].issuer
                : undefined,
          })
    }
  }

  return (
    <ActionRightFlyoutContent
      title={translate(
        showRequestBudget ? 'Request a Budget' : 'Create a Budget',
      )}
      specificElements={{
        subtitle: translate('Recurring'),
        imageSrc: budgetRecurringActiveImage,
        changeLink: {
          ariaLabelSource: 'Change Budget Type',
          changeMessage: translate('Change Budget Type'),
          onChangeClick: (): void => {
            props.setScreen(BudgetScreen.Pick)
          },
        },
      }}
      permissions={{
        isBudget: true,
      }}
    >
      <BudgetForm
        buttonText={translate(
          showRequestBudget ? 'Request a Budget' : 'Create a Budget',
        )}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.postingCreditCard}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={
          showRequestBudget ? translate('Request from') : translate('Recipient')
        }
        recurrenceEditable={true}
        rcnOptions={props.rcnOptions}
        initialFlatFields={props.initialFlatFields}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        isFormDisabled={props.isFormDisabled}
        suggestedProps={{
          showSuggested: !!(
            user?.hasOrgAdminPermissions ||
            user?.organizationRole === OrganizationRoles.cardManager
          ),
          showOwnUserInSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.delegateCreditCardTo]}
        unableToAddNewUser={!user?.hasOrgAdminPermissions}
        isRequestBudget={showRequestBudget}
      />
    </ActionRightFlyoutContent>
  )
}

export default BudgetRecurringContent
